import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  CIVILITES_CONTACT,
  CONTACT_STATUT,
  Contact,
  ContactStatutHistorique,
  ContactTelephone,
  labelStatutContact,
  peutRefuserContact,
  peutReouvrirContact,
  telephoneParDefaut,
  telephonesActifs,
} from '../../../core/models/contact.model';
import { ContactService } from '../../../core/services/contact.service';
import { FlashService } from '../../../core/services/flash.service';
import { APP_ROUTES } from '../../../core/constants/app-routes';
import { FLASH_MESSAGES } from '../../../core/constants/flash-messages';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { I18nValuePipe } from '../../../core/pipes/i18n-value.pipe';
import { HelpTipComponent } from '../../../shared/help-tip/help-tip.component';

@Component({
    selector: 'app-fiche-contact',
    templateUrl: './fiche-contact.component.html',
    styleUrls: ['./fiche-contact.component.css'],
    imports: [RouterLink, FaIconComponent, FormsModule, ReactiveFormsModule, DatePipe, TranslateModule, I18nValuePipe, HelpTipComponent]
})
export class FicheContactComponent implements OnInit {
  @ViewChild('deleteDialog')
  private readonly deleteDialog?: ElementRef<HTMLDialogElement>;

  @ViewChild('refuseDialog')
  private readonly refuseDialog?: ElementRef<HTMLDialogElement>;

  @ViewChild('reopenDialog')
  private readonly reopenDialog?: ElementRef<HTMLDialogElement>;

  contact?: Contact;
  loading = true;
  error = false;
  saving = false;
  editMode = false;
  formError = false;
  phoneError = false;
  phoneSaving = false;
  confirmingDelete = false;
  deleting = false;
  deleteError = false;
  confirmingRefuse = false;
  refusing = false;
  refuseError = false;
  confirmingReopen = false;
  reopening = false;
  reopenError = false;
  readonly civilites = CIVILITES_CONTACT;
  readonly routes = APP_ROUTES;

  readonly form = this.fb.nonNullable.group({
    civilite: [''],
    nom: ['', Validators.required],
    prenom: ['', Validators.required],
    mail: [''],
    notes: [''],
  });

  readonly phoneForm = this.fb.nonNullable.group({
    numero: ['', Validators.required],
    libelle: [''],
  });

  readonly refuseForm = this.fb.nonNullable.group({
    motif: ['', Validators.required],
  });

  private contactId?: string;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly contactService: ContactService,
    private readonly flashService: FlashService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading = false;
      this.error = true;
      return;
    }
    this.contactId = id;
    this.load();
  }

  defaultPhone(): ContactTelephone | undefined {
    return telephoneParDefaut(this.contact);
  }

  phones(): ContactTelephone[] {
    return telephonesActifs(this.contact);
  }

  statutLabel(): string {
    return labelStatutContact(this.contact?.statutDto);
  }

  canRefuse(): boolean {
    return peutRefuserContact(this.contact?.statutDto);
  }

  canReopen(): boolean {
    return peutReouvrirContact(this.contact?.statutDto);
  }

  historique(): ContactStatutHistorique[] {
    return this.contact?.historiqueStatutsDto ?? [];
  }

  statutEventLabel(statut?: string | null): string {
    return labelStatutContact(statut as Contact['statutDto']);
  }

  motifRefusLabel(): string {
    return this.contact?.statutDto === CONTACT_STATUT.REFUSE
      ? 'contacts.fiche.motifRefus'
      : 'contacts.fiche.dernierMotifRefus';
  }

  dateRefusLabel(): string {
    return this.contact?.statutDto === CONTACT_STATUT.REFUSE
      ? 'contacts.fiche.dateRefus'
      : 'contacts.fiche.dateDernierRefus';
  }

  startEdit(): void {
    if (!this.contact || this.saving) {
      return;
    }
    this.editMode = true;
    this.formError = false;
    this.form.reset({
      civilite: this.contact.civiliteDto ?? '',
      nom: this.contact.nomDto ?? '',
      prenom: this.contact.prenomDto ?? '',
      mail: this.contact.mailDto ?? '',
      notes: this.contact.notesDto ?? '',
    });
  }

  cancelEdit(): void {
    if (this.saving) {
      return;
    }
    this.editMode = false;
    this.formError = false;
  }

  save(): void {
    if (!this.contact || !this.contactId || this.saving) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    const payload: Contact = {
      ...this.contact,
      idContactDto: this.contactId,
      civiliteDto: value.civilite.trim() || undefined,
      nomDto: value.nom.trim(),
      prenomDto: value.prenom.trim(),
      mailDto: value.mail.trim() || undefined,
      notesDto: value.notes.trim() || undefined,
      statutDto: this.contact.statutDto,
      actifDto: this.contact.actifDto,
      telephonesDto: undefined,
    };
    this.saving = true;
    this.formError = false;
    this.contactService.update(payload).subscribe({
      next: (saved) => {
        this.contact = saved;
        this.saving = false;
        this.editMode = false;
      },
      error: () => {
        this.saving = false;
        this.formError = true;
      },
    });
  }

  addPhone(): void {
    if (!this.contact || !this.contactId || this.phoneSaving) {
      return;
    }
    if (this.phoneForm.invalid) {
      this.phoneForm.markAllAsTouched();
      return;
    }
    const value = this.phoneForm.getRawValue();
    const numero = value.numero.trim();
    if (!numero) {
      return;
    }

    const next = [...(this.contact.telephonesDto ?? [])];
    const phone = new ContactTelephone();
    phone.numeroDto = numero;
    phone.libelleDto = value.libelle.trim() || undefined;
    phone.actifDto = true;
    phone.parDefautDto = next.filter((item) => item.actifDto !== false).length === 0;
    next.push(phone);

    this.persistPhones(next, () => {
      this.phoneForm.reset({ numero: '', libelle: '' });
    });
  }

  setDefaultPhone(phone: ContactTelephone): void {
    if (!this.contact || !phone || this.phoneSaving) {
      return;
    }
    const next = (this.contact.telephonesDto ?? []).map((item) => ({
      ...item,
      parDefautDto:
        item.idTelephoneDto && phone.idTelephoneDto
          ? item.idTelephoneDto === phone.idTelephoneDto
          : item.numeroDto === phone.numeroDto &&
            item.libelleDto === phone.libelleDto,
    }));
    this.persistPhones(next);
  }

  archivePhone(phone: ContactTelephone): void {
    if (!this.contact || !phone || this.phoneSaving) {
      return;
    }
    const next = (this.contact.telephonesDto ?? []).map((item) => {
      const same =
        item.idTelephoneDto && phone.idTelephoneDto
          ? item.idTelephoneDto === phone.idTelephoneDto
          : item.numeroDto === phone.numeroDto &&
            item.libelleDto === phone.libelleDto;
      if (!same) {
        return { ...item };
      }
      return {
        ...item,
        actifDto: false,
        parDefautDto: false,
      };
    });

    const actifs = next.filter((item) => item.actifDto !== false);
    if (actifs.length > 0 && !actifs.some((item) => item.parDefautDto)) {
      actifs[0].parDefautDto = true;
    }
    this.persistPhones(next);
  }

  askDelete(): void {
    if (
      !this.contact ||
      this.saving ||
      this.phoneSaving ||
      this.deleting ||
      this.refusing ||
      this.reopening
    ) {
      return;
    }
    this.deleteError = false;
    this.confirmingDelete = true;
    queueMicrotask(() => this.deleteDialog?.nativeElement.showModal());
  }

  cancelDelete(): void {
    if (this.deleting) {
      return;
    }
    this.deleteDialog?.nativeElement.close();
    this.confirmingDelete = false;
  }

  onDeleteDialogCancel(event: Event): void {
    if (this.deleting) {
      event.preventDefault();
      return;
    }
    this.confirmingDelete = false;
  }

  onDeleteDialogBackdropClick(event: MouseEvent): void {
    this.closeDeleteDialogFromBackdrop(event);
  }

  onDeleteDialogBackdropKeydown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.closeDeleteDialogFromBackdrop(event);
  }

  private closeDeleteDialogFromBackdrop(event: Event): void {
    if (event.target !== event.currentTarget || this.deleting) {
      return;
    }
    this.cancelDelete();
  }

  confirmDelete(): void {
    if (!this.contactId || this.deleting || !this.confirmingDelete) {
      return;
    }
    this.deleting = true;
    this.deleteError = false;
    this.contactService.delete(this.contactId).subscribe({
      next: () => {
        this.deleting = false;
        this.deleteDialog?.nativeElement.close();
        this.confirmingDelete = false;
        this.flashService.success(FLASH_MESSAGES.contactSupprime);
        void this.router.navigate([APP_ROUTES.contacts]);
      },
      error: () => {
        this.deleting = false;
        this.deleteError = true;
      },
    });
  }

  askRefuse(): void {
    if (
      !this.contact ||
      !this.canRefuse() ||
      this.saving ||
      this.phoneSaving ||
      this.deleting ||
      this.refusing
    ) {
      return;
    }
    this.refuseError = false;
    this.confirmingRefuse = true;
    this.refuseForm.reset({ motif: '' });
    queueMicrotask(() => this.refuseDialog?.nativeElement.showModal());
  }

  cancelRefuse(): void {
    if (this.refusing) {
      return;
    }
    this.refuseDialog?.nativeElement.close();
    this.confirmingRefuse = false;
  }

  onRefuseDialogCancel(event: Event): void {
    if (this.refusing) {
      event.preventDefault();
      return;
    }
    this.confirmingRefuse = false;
  }

  onRefuseDialogBackdropClick(event: MouseEvent): void {
    this.closeRefuseDialogFromBackdrop(event);
  }

  onRefuseDialogBackdropKeydown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.closeRefuseDialogFromBackdrop(event);
  }

  private closeRefuseDialogFromBackdrop(event: Event): void {
    if (event.target !== event.currentTarget || this.refusing) {
      return;
    }
    this.cancelRefuse();
  }

  confirmRefuse(): void {
    if (!this.contactId || this.refusing || !this.confirmingRefuse) {
      return;
    }
    if (this.refuseForm.invalid) {
      this.refuseForm.markAllAsTouched();
      return;
    }
    const motif = this.refuseForm.getRawValue().motif.trim();
    if (!motif) {
      return;
    }
    this.refusing = true;
    this.refuseError = false;
    this.contactService.refuser(this.contactId, motif).subscribe({
      next: (saved) => {
        this.contact = saved;
        this.refusing = false;
        this.refuseDialog?.nativeElement.close();
        this.confirmingRefuse = false;
        this.flashService.success(FLASH_MESSAGES.contactRefuse);
      },
      error: () => {
        this.refusing = false;
        this.refuseError = true;
      },
    });
  }

  askReopen(): void {
    if (
      !this.contact ||
      !this.canReopen() ||
      this.saving ||
      this.phoneSaving ||
      this.deleting ||
      this.refusing ||
      this.reopening
    ) {
      return;
    }
    this.reopenError = false;
    this.confirmingReopen = true;
    queueMicrotask(() => this.reopenDialog?.nativeElement.showModal());
  }

  cancelReopen(): void {
    if (this.reopening) {
      return;
    }
    this.reopenDialog?.nativeElement.close();
    this.confirmingReopen = false;
  }

  onReopenDialogCancel(event: Event): void {
    if (this.reopening) {
      event.preventDefault();
      return;
    }
    this.confirmingReopen = false;
  }

  onReopenDialogBackdropClick(event: MouseEvent): void {
    this.closeReopenDialogFromBackdrop(event);
  }

  onReopenDialogBackdropKeydown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.closeReopenDialogFromBackdrop(event);
  }

  private closeReopenDialogFromBackdrop(event: Event): void {
    if (event.target !== event.currentTarget || this.reopening) {
      return;
    }
    this.cancelReopen();
  }

  confirmReopen(): void {
    if (!this.contactId || this.reopening || !this.confirmingReopen) {
      return;
    }
    this.reopening = true;
    this.reopenError = false;
    this.contactService.reouvrir(this.contactId).subscribe({
      next: (saved) => {
        this.contact = saved;
        this.reopening = false;
        this.reopenDialog?.nativeElement.close();
        this.confirmingReopen = false;
        this.flashService.success(FLASH_MESSAGES.contactRouvert);
      },
      error: () => {
        this.reopening = false;
        this.reopenError = true;
      },
    });
  }

  private persistPhones(
    telephones: ContactTelephone[],
    after?: () => void
  ): void {
    if (!this.contact || !this.contactId) {
      return;
    }
    const payload: Contact = {
      ...this.contact,
      idContactDto: this.contactId,
      telephonesDto: telephones,
    };
    this.phoneSaving = true;
    this.phoneError = false;
    this.contactService.update(payload).subscribe({
      next: (saved) => {
        this.contact = saved;
        this.phoneSaving = false;
        after?.();
      },
      error: () => {
        this.phoneSaving = false;
        this.phoneError = true;
      },
    });
  }

  private load(): void {
    if (!this.contactId) {
      return;
    }
    this.loading = true;
    this.error = false;
    this.contactService.findById(this.contactId).subscribe({
      next: (contact) => {
        this.contact = contact;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = true;
      },
    });
  }
}
